import React from 'react';
import { FormattedMessage } from 'react-intl';
import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { startOfDay } from 'date-fns';
import { IMeetingsDAO } from '../../../../../api/meetings.dao';
import { IEventDetailsDTO } from '../../../../../api/events.dao';
import { IAlertsService } from '../../../../../infrastructure/alerts';
import { Space } from '../../../../../constants/html-character-entities';
import Text from '../../../../global/text/text';
import { utcToZonedTime } from '../../../../../infrastructure/utils/date-fns-overrides.utils';
import { IApiDateConverter } from '../../../../../infrastructure/api-date-converter';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import { DateFnsFormats, DateFormats } from '../../../../../infrastructure/date-formatter';
import { CalendarCancellationWarning } from './calendar-cancellation-warning';

class BulkActionModalViewModel {
    @mobx.observable
    private availableDates: { dateNumber: number; dateToRender: string }[] = [];

    @mobx.observable
    private selectedDates: Set<number> = new Set();

    @mobx.observable
    private calendarCancellationDataAvailable: boolean = false;

    private cancellationCountByDay: Map<number, number> = new Map();

    public constructor(
        public readonly accountId: number,
        public readonly event: IEventDetailsDTO,
        public readonly onCancel: () => void,
        public readonly onSubmit: (...args) => void,
        private readonly requiresConfirmation: boolean,
        public readonly formLabelText: React.ReactNode,
        public readonly modalTitle: React.ReactNode,
        public readonly selectedDatesLabelProps: { id: string; defaultMessage: string },
        private readonly excludeCanceledDates?: boolean,
        public readonly alertMessage?: string,
        public readonly alertConfirmButtonText?: React.ReactNode,
        @IoC.Inject private readonly meetingsDAO: IMeetingsDAO = IoC.Container.get(IMeetingsDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter = IoC.Container.get(IApiDateConverter),
        @IoC.Inject private readonly dateTimeFormatter: DateTimeFormatter = IoC.Container.get(DateTimeFormatter)
    ) {}

    @mobx.action
    public async initialize() {
        const response = await (this.excludeCanceledDates
            ? this.getStartDateTimesWithoutCancelled
            : this.getAllStartDateTimes)();
        this.setAvailableDates(response.dates);
        this.setCalendarCancellationCounts(response.dateDetails);
    }

    private getAllStartDateTimes = () => {
        return this.meetingsDAO.getMeetingsStartDateTimes({
            eventId: this.event.eventId,
            accountId: this.accountId
        });
    };

    private getStartDateTimesWithoutCancelled = () => {
        return this.meetingsDAO.getScheduledMeetingsStartDateTimes({
            eventId: this.event.eventId,
            accountId: this.accountId
        });
    };

    private formatSelectedDatesForAlert = () => {
        return [...this.getSelectedDates()]
            .sort((a, b) => a - b)
            .map(date => this.dateTimeFormatter.dateToFormattedString(new Date(date), DateFormats.DayAndMonthFormat))
            .join(', ')
            .replace(/,(?=[^,]*$)/, ' and');
    };

    private setAvailableDates = (dates: string[]) => {
        const uniqueDaysDates = [...new Set(dates.map(date => +startOfDay(utcToZonedTime(date, this.event.timeZone))))];
        const availableDates = uniqueDaysDates
            .sort((a, b) => a - b)
            .map((zonedDateNumber: number) => ({
                dateNumber: zonedDateNumber,
                dateToRender: this.dateTimeFormatter.dateToFormattedString(
                    new Date(zonedDateNumber),
                    DateFnsFormats.StringifiedFullDayMonthNumericDateYear
                )
            }));

        mobx.runInAction(() => {
            this.availableDates = availableDates;
        });
    };

    @mobx.action
    private setCalendarCancellationCounts = (dateDetails?: { dateTime: string; calendarCancellationCount: number }[]) => {
        const countByDay = new Map<number, number>();

        dateDetails?.forEach(({ dateTime, calendarCancellationCount }) => {
            const day = +startOfDay(utcToZonedTime(dateTime, this.event.timeZone));
            countByDay.set(day, (countByDay.get(day) ?? 0) + calendarCancellationCount);
        });

        this.cancellationCountByDay = countByDay;
        this.calendarCancellationDataAvailable = dateDetails !== undefined;
    };

    public shouldShowCalendarCancellationWarning = (): boolean => {
        return this.calendarCancellationDataAvailable
            && this.getCalendarCancellationCountForSelectedDates() > 0;
    };

    public getCalendarCancellationCountForSelectedDates = (): number => {
        return [...this.selectedDates].reduce(
            (sum, day) => sum + (this.cancellationCountByDay.get(day) ?? 0),
            0
        );
    };

    public getAvailableDates = () => {
        return this.availableDates;
    };

    public getSelectedDates = () => {
        return this.selectedDates;
    };

    @mobx.action
    public handleDatesChange = ({ target: { value, checked } }: React.ChangeEvent<HTMLInputElement>) => {
        if (checked) {
            this.selectedDates.add(+value);
        } else {
            this.selectedDates.delete(+value);
        }
    };

    @mobx.action
    public selectAll = ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
        if (checked) {
            this.selectedDates = new Set(this.availableDates.map(date => date.dateNumber));
        } else {
            this.selectedDates.clear();
        }
    };

    public openDatesAlert = () => {
        this.requiresConfirmation
            ? this.alertsService.confirm({
                message: (
                    <>
                        <Text className='alert-message' message={this.alertMessage} />
                        <Space />
                        <Text className='alert-message--danger' message={this.formatSelectedDatesForAlert()} />?
                        <Space />
                        {this.shouldShowCalendarCancellationWarning() && (
                            <CalendarCancellationWarning
                                count={this.getCalendarCancellationCountForSelectedDates()}
                                className='alert-message'
                            />
                        )}
                    </>
                ),
                confirmButtonProps: {
                    text: this.alertConfirmButtonText,
                    modifier: 'confirm-warn',
                    handler: this.bulkActionApiCall
                },
                cancelButtonProps: {
                    text: (
                        <FormattedMessage
                            id='oe.common.noGoBack'
                            description='Button label'
                            defaultMessage='No, go back'
                        />
                    ),
                    modifier: 'cancel',
                    handler: this.onCancel
                }
            })
            : this.bulkActionApiCall();
    };

    private bulkActionApiCall = async () => {
        const dates = [...this.getSelectedDates()].map(zonedDate =>
            this.apiDateConverter.dateToAPIString(new Date(+zonedDate), this.event.timeZone)
        );
        this.onCancel();
        await this.onSubmit(dates);
    };
}

export { BulkActionModalViewModel };
